/**
 * Lifecycle remote sync — PATCH lifecycleStatus/outcome to the lot endpoint.
 *
 * @module portal/modules/apiOperations/lifecycle
 */

import type { Lot, State } from '../types';
import { apiUrl } from './common';

export interface LifecyclePatchBody {
  lifecycleStatus: string;
  outcome?: {
    hammerPrice?: number | null;
    feesPaid?: number | null;
    decidedAt?: string;
    salePrice?: number | null;
    saleChannel?: string;
    soldAt?: string;
  };
}

export async function syncLifecycleRemote(
  id: string,
  body: LifecyclePatchBody,
  state: State,
  ensureAccessToken: (retry?: boolean) => Promise<string | null>,
  mapRemoteDocument: (data: Record<string, unknown>) => Lot | null | undefined,
  retry = false
): Promise<Lot | null> {
  let token: string | null;
  try {
    token = await ensureAccessToken(retry);
  } catch {
    throw new Error('Unable to acquire access token.');
  }
  if (!token) throw new Error('Unable to acquire access token.');

  const endpoint = apiUrl('extensions/scrapes/' + encodeURIComponent(id));
  const response = await fetch(endpoint, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
    body: JSON.stringify(body),
  });

  if (response.status === 401 && !retry) {
    (state as { extAccessToken?: string | null }).extAccessToken = null;
    return syncLifecycleRemote(id, body, state, ensureAccessToken, mapRemoteDocument, true);
  }
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || 'Failed to update lot status (' + response.status + ')');
  }

  const data = await response.json();
  return mapRemoteDocument(data && data.document ? data.document : data) ?? null;
}
